import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { getAccount, updateAccount, changePassword, logoutUser } from '../api/auth';
import { getAvatar, createAvatar, updateAvatar } from '../api/avatars';
import { getActivities, getTotalPoints } from '../api/activities';
import { getPendingReceivedRequests } from '../api/social';
import { getHotPotatoUnreadCount } from '../api/hotPotato';
import { getMyStreak } from '../api/users';
import { getMarket } from '../api/market';
import { clearAuthenticated } from '../storage';
import AvatarSelector from '../components/AvatarSelector';
import Avatar from '../components/Avatar';
import NotificationBell from '../components/NotificationBell';
import { EXCLUSIVE_AVATARS } from '../constants/avatars';
import { fetchDefaultAvatarSvg, formatActivityName } from '../utils';
import { passwordError } from '../utils/password';
import PasswordField, { PasswordMatchHint } from '../components/PasswordField';
import '../styles/ProfilePage.css';

const ProfilePage = () => {
    const history = useHistory();
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        avatar: '', // Will hold SVG content
    });
    const [currentAvatarSvg, setCurrentAvatarSvg] = useState('');
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [isSavingPassword, setIsSavingPassword] = useState(false);

    // Activity state
    const [activities, setActivities] = useState([]);
    const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [totalPoints, setTotalPoints] = useState(null);
    const [streak, setStreak] = useState(null);
    const [pendingRequestCount, setPendingRequestCount] = useState(0);
    const [hotPotatoUnreadCount, setHotPotatoUnreadCount] = useState(0);
    // SpudMarket avatars, flagged locked until the user has bought them.
    const [exclusiveAvatars, setExclusiveAvatars] = useState(
        EXCLUSIVE_AVATARS.map((a) => ({ ...a, locked: true }))
    );

    useEffect(() => {
        fetchProfile();
        fetchActivities();
        fetchTotalPoints();
        fetchPendingRequests();
        fetchHotPotatoUnread();
        fetchStreak();
        fetchOwnedAvatars();
    }, []);

    // Re-fetch activities when filters change
    useEffect(() => {
        fetchActivities();
    }, [filterStartDate, filterEndDate]);

    const fetchTotalPoints = async () => {
        try {
            const pts = await getTotalPoints();
            setTotalPoints(pts);
        } catch (err) {
            console.error('Failed to load total points:', err);
        }
    };

    const fetchStreak = async () => {
        try {
            const s = await getMyStreak();
            setStreak(s);
        } catch (err) {
            console.error('Failed to load streak:', err);
        }
    };

    const fetchOwnedAvatars = async () => {
        try {
            const market = await getMarket();
            const owned = new Set(
                (market.items || [])
                    .filter((item) => item.item_type === 'avatar' && item.owned)
                    .map((item) => item.payload)
            );
            setExclusiveAvatars(
                EXCLUSIVE_AVATARS.map((a) => ({ ...a, locked: !owned.has(a.filename) }))
            );
        } catch (err) {
            console.error('Failed to load owned avatars:', err);
        }
    };

    const fetchPendingRequests = async () => {
        try {
            const data = await getPendingReceivedRequests();
            const requests = Array.isArray(data.received_requests) ? data.received_requests : [];
            setPendingRequestCount(requests.length);
        } catch (err) {
            console.error('Failed to load pending requests:', err);
        }
    };

    const fetchHotPotatoUnread = async () => {
        try {
            const data = await getHotPotatoUnreadCount();
            setHotPotatoUnreadCount(Number(data.unread_count) || 0);
        } catch (err) {
            console.error('Failed to load Hot Potato unread count:', err);
        }
    };

    const fetchActivities = async () => {
        setActivitiesLoading(true);
        try {
            const data = await getActivities(filterStartDate || undefined, filterEndDate || undefined);
            setActivities(data || []);
        } catch (err) {
            console.error('Failed to load activities:', err);
        } finally {
            setActivitiesLoading(false);
        }
    };

    // Build activity type counts from loaded activities
    const activityTypeCounts = useMemo(() => {
        return activities.reduce((acc, a) => {
            acc[a.activity] = (acc[a.activity] || 0) + 1;
            return acc;
        }, {});
    }, [activities]);

    // Catalog display name per activity id, with a fallback for retired catalog entries.
    const activityNames = useMemo(() => {
        return activities.reduce((acc, a) => {
            if (!acc[a.activity]) acc[a.activity] = a.activity_name || formatActivityName(a.activity);
            return acc;
        }, {});
    }, [activities]);

    const handleActivityTypeClick = (type) => {
        setSelectedActivityTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const clearAllFilters = () => {
        setSelectedActivityTypes([]);
        setFilterStartDate('');
        setFilterEndDate('');
    };

    // Filter activities by selected types (date filtering is done server-side)
    const filteredActivities = useMemo(() => {
        let list = activities;
        if (selectedActivityTypes.length > 0) {
            list = list.filter(a => selectedActivityTypes.includes(a.activity));
        }
        return list.sort((a, b) => new Date(b.date + 'T00:00') - new Date(a.date + 'T00:00'));
    }, [activities, selectedActivityTypes]);

    // Compute weekly points for bar chart (last 8 weeks)
    const weeklyPoints = useMemo(() => {
        const weeks = [];
        const now = new Date();
        for (let i = 7; i >= 0; i--) {
            const weekEnd = new Date(now);
            weekEnd.setDate(now.getDate() - i * 7);
            const weekStart = new Date(weekEnd);
            weekStart.setDate(weekEnd.getDate() - 6);
            weeks.push({ start: weekStart, end: weekEnd, points: 0 });
        }

        activities.forEach(a => {
            const d = new Date(a.date + 'T00:00');
            const pts = a.points || 0;
            for (const w of weeks) {
                if (d >= w.start && d <= w.end) {
                    w.points += pts;
                    break;
                }
            }
        });

        const maxPts = Math.max(...weeks.map(w => w.points), 1);
        return weeks.map(w => ({
            label: `${w.start.getMonth() + 1}/${w.start.getDate()}`,
            points: Math.round(w.points * 100) / 100,
            pct: (w.points / maxPts) * 100,
        }));
    }, [activities]);

    const fetchProfile = async () => {
        try {
            const data = await getAccount();
            setUser(data.user);
            
            // Fetch the actual avatar SVG data
            if (data.user.avatar_id) {
                try {
                    const avatarData = await getAvatar(data.user.avatar_id);
                    const svgContent = avatarData.avatar.svg_data;
                    setCurrentAvatarSvg(svgContent);
                    setFormData({
                        username: data.user.username,
                        email: data.user.email,
                        avatar: svgContent,
                    });
                } catch (err) {
                    console.error('Failed to load avatar:', err);
                    // Load default avatar as fallback
                    const defaultSvg = await fetchDefaultAvatarSvg();
                    setCurrentAvatarSvg(defaultSvg);
                    setFormData({
                        username: data.user.username,
                        email: data.user.email,
                        avatar: defaultSvg,
                    });
                }
            } else {
                // No avatar yet, load default
                const defaultSvg = await fetchDefaultAvatarSvg();
                setCurrentAvatarSvg(defaultSvg);
                setFormData({
                    username: data.user.username,
                    email: data.user.email,
                    avatar: defaultSvg,
                });
            }
        } catch (err) {
            setError('Failed to load profile');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        
        try {
            const updates = {};
            if (formData.username !== user.username) updates.username = formData.username;
            if (formData.email !== user.email) updates.email = formData.email;
            
            // Handle avatar update separately through avatar API
            let avatarUpdated = false;
            if (formData.avatar !== currentAvatarSvg) {
                try {
                    if (user.avatar_id) {
                        // Update existing avatar
                        await updateAvatar(user.avatar_id, formData.avatar);
                    } else {
                        // Create new avatar
                        const avatarResult = await createAvatar(formData.avatar);
                        // The createAvatar endpoint automatically updates the user profile
                    }
                    avatarUpdated = true;
                    setCurrentAvatarSvg(formData.avatar);
                } catch (avatarErr) {
                    console.error('Failed to update avatar:', avatarErr);
                    setError('Failed to update avatar: ' + (avatarErr.message || 'Unknown error'));
                    return;
                }
            }
            
            if (Object.keys(updates).length === 0 && !avatarUpdated) {
                setError('No changes to save');
                return;
            }

            // Update other profile fields if any
            if (Object.keys(updates).length > 0) {
                const response = await updateAccount(updates);
                setUser(response.user);
            } else if (avatarUpdated) {
                // Refresh user data to get updated avatar_id
                await fetchProfile();
            }
            
            setSuccess('Profile updated successfully');
            setIsEditing(false);
            
            // Dispatch custom event to notify other components (like Nav bar) to refresh
            if (avatarUpdated) {
                window.dispatchEvent(new CustomEvent('avatarUpdated', { 
                    detail: { avatar: formData.avatar } 
                }));
            }
        } catch (err) {
            setError(err.message || 'Failed to update profile');
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const weakness = passwordError(passwordData.newPassword);
        if (weakness) {
            setError(weakness);
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsSavingPassword(true);
        try {
            await changePassword(passwordData.newPassword);
            setSuccess('Password updated successfully');
            setPasswordData({ newPassword: '', confirmPassword: '' });
            setIsChangingPassword(false);
        } catch (err) {
            setError(err.message || 'Failed to update password');
        } finally {
            setIsSavingPassword(false);
        }
    };

    const handleLogout = async () => {
        // Ask the server to clear the httpOnly auth cookies first,
        // then clear local session state and redirect.
        await logoutUser();
        clearAuthenticated();
        history.push('/login');
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setFormData({ username: user.username, email: user.email, avatar: currentAvatarSvg });
        setError('');
        setSuccess('');
    };

    const cancelPasswordChange = () => {
        setIsChangingPassword(false);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setError('');
        setSuccess('');
    };

    if (!user) return (
        <div className="pp-page">
            <div className="pp-container">
                <div className="pp-loading">
                    <div className="pp-spinner" />
                    <p>Loading profile...</p>
                </div>
            </div>
        </div>
    );

    const showingAccount = !isEditing && !isChangingPassword;

    return (
        <div className="pp-page">
            <div className="pp-container">
                <div className="pp-header">
                    <h2>Profile</h2>
                    <div className="pp-header-actions">
                        <NotificationBell />
                    </div>
                </div>

                {(success || error) && (
                    <div className="pp-alerts">
                        {success && <div className="pp-success">✓ {success}</div>}
                        {error && <div className="pp-error">⚠ {error}</div>}
                    </div>
                )}

                {showingAccount && (
                    <div className="pp-summary">
                        <div className="pp-summary-identity">
                            <div className="pp-avatar-ring">
                                <Avatar
                                    svgContent={currentAvatarSvg}
                                    alt="User avatar"
                                    className="pp-avatar"
                                />
                            </div>
                            <div className="pp-summary-text">
                                <h3>{user.username}</h3>
                                <p>{user.email}</p>
                            </div>
                        </div>
                        <div className="pp-summary-stats">
                            <div className="pp-stat pp-stat--points">
                                <span className="pp-stat-value">{totalPoints != null ? totalPoints : '—'}</span>
                                <span className="pp-stat-label">All-time SP</span>
                            </div>
                            <div className="pp-stat pp-stat--streak">
                                <span className="pp-stat-value">{streak !== null ? streak : '—'}</span>
                                <span className="pp-stat-label">Day streak</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="pp-content">
                    {isEditing && (
                        <div className="pp-section">
                            <p className="pp-section-title">Edit profile</p>
                            <form onSubmit={handleUpdateProfile} className="pp-form">
                                <div className="pp-form-group">
                                    <label htmlFor="profile-username">Username</label>
                                    <input
                                        id="profile-username"
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your username"
                                    />
                                </div>
                                <div className="pp-form-group">
                                    <label htmlFor="profile-email">Email address</label>
                                    <input
                                        id="profile-email"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your email"
                                    />
                                </div>
                                <AvatarSelector
                                    selectedAvatar={formData.avatar}
                                    extraAvatars={exclusiveAvatars}
                                    onAvatarSelect={(_filename, svgContent) =>
                                        setFormData(prev => ({ ...prev, avatar: svgContent }))
                                    }
                                />
                                <div className="pp-form-actions">
                                    <button type="submit" className="sc-btn sc-btn--primary">Save changes</button>
                                    <button type="button" className="sc-btn sc-btn--secondary" onClick={cancelEditing}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isChangingPassword && (
                        <div className="pp-section">
                            <p className="pp-section-title">Change password</p>
                            <form onSubmit={handleUpdatePassword} className="pp-form">
                                <PasswordField
                                    id="profile-new-password"
                                    name="newPassword"
                                    label="New password"
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter new password"
                                    autoComplete="new-password"
                                    disabled={isSavingPassword}
                                    showRules
                                />
                                <div className="auth-field-stack">
                                    <PasswordField
                                        id="profile-confirm-password"
                                        name="confirmPassword"
                                        label="Confirm password"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Confirm new password"
                                        autoComplete="new-password"
                                        disabled={isSavingPassword}
                                    />
                                    <PasswordMatchHint
                                        password={passwordData.newPassword}
                                        confirm={passwordData.confirmPassword}
                                    />
                                </div>
                                <div className="pp-form-actions">
                                    <button type="submit" className="sc-btn sc-btn--primary" disabled={isSavingPassword}>
                                        {isSavingPassword ? 'Updating...' : 'Update password'}
                                    </button>
                                    <button type="button" className="sc-btn sc-btn--secondary" onClick={cancelPasswordChange} disabled={isSavingPassword}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {showingAccount && (
                        <div className="pp-section">
                            <p className="pp-section-title">Account</p>
                            <div className="pp-list">
                                <button type="button" className="pp-action" onClick={() => setIsEditing(true)}>
                                    <span className="pp-action-icon" aria-hidden="true">✏️</span>
                                    <span className="pp-action-body">
                                        <span className="pp-action-name">Edit profile</span>
                                        <span className="pp-action-desc">Username, email, and avatar</span>
                                    </span>
                                    <span className="pp-action-arrow" aria-hidden="true">›</span>
                                </button>
                                <button type="button" className="pp-action" onClick={() => setIsChangingPassword(true)}>
                                    <span className="pp-action-icon" aria-hidden="true">🔒</span>
                                    <span className="pp-action-body">
                                        <span className="pp-action-name">Change password</span>
                                        <span className="pp-action-desc">Update the password for this account</span>
                                    </span>
                                    <span className="pp-action-arrow" aria-hidden="true">›</span>
                                </button>
                                <button type="button" className="pp-action" onClick={() => history.push('/friends')}>
                                    <span className="pp-action-icon" aria-hidden="true">👥</span>
                                    <span className="pp-action-body">
                                        <span className="pp-action-name">Friends</span>
                                        <span className="pp-action-desc">
                                            {pendingRequestCount > 0
                                                ? `${pendingRequestCount} request${pendingRequestCount === 1 ? '' : 's'} waiting`
                                                : 'Requests and your friend list'}
                                        </span>
                                    </span>
                                    {pendingRequestCount > 0 && (
                                        <span className="pp-action-badge">{pendingRequestCount}</span>
                                    )}
                                    <span className="pp-action-arrow" aria-hidden="true">›</span>
                                </button>
                                <button type="button" className="pp-action" onClick={() => history.push('/hot-potato')}>
                                    <span className="pp-action-icon" aria-hidden="true">🔥</span>
                                    <span className="pp-action-body">
                                        <span className="pp-action-name">Hot Potatoes</span>
                                        <span className="pp-action-desc">
                                            {hotPotatoUnreadCount > 0
                                                ? `${hotPotatoUnreadCount} new`
                                                : 'Sent and received'}
                                        </span>
                                    </span>
                                    {hotPotatoUnreadCount > 0 && (
                                        <span className="pp-action-badge">{hotPotatoUnreadCount}</span>
                                    )}
                                    <span className="pp-action-arrow" aria-hidden="true">›</span>
                                </button>
                                <button type="button" className="pp-action pp-action--danger" onClick={handleLogout}>
                                    <span className="pp-action-icon" aria-hidden="true">↪</span>
                                    <span className="pp-action-body">
                                        <span className="pp-action-name">Log out</span>
                                        <span className="pp-action-desc">Sign out of SpudClub on this device</span>
                                    </span>
                                    <span className="pp-action-arrow" aria-hidden="true">›</span>
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="pp-section">
                        <p className="pp-section-title">Weekly SP</p>
                        <div className="pp-weekly-chart" aria-hidden="true">
                            {weeklyPoints.map((w, i) => (
                                <div className="pp-chart-bar-wrapper" key={i}>
                                    <span className="pp-chart-bar-value">{w.points > 0 ? w.points : ''}</span>
                                    <div className="pp-chart-bar" style={{ height: `${Math.max(w.pct, 2)}%` }} />
                                    <span className="pp-chart-bar-label">{w.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pp-section">
                        <div className="pp-section-head">
                            <p className="pp-section-title">Activity</p>
                            {selectedActivityTypes.length > 0 && (
                                <span className="pp-filter-hint">{selectedActivityTypes.length} selected</span>
                            )}
                        </div>

                        {Object.keys(activityTypeCounts).length > 0 && (
                            <div className="pp-chips">
                                {Object.entries(activityTypeCounts).map(([activity, count]) => {
                                    const selected = selectedActivityTypes.includes(activity);
                                    return (
                                        <button
                                            key={activity}
                                            type="button"
                                            className={`pp-chip ${selected ? 'is-active' : ''}`}
                                            onClick={() => handleActivityTypeClick(activity)}
                                            aria-pressed={selected}
                                        >
                                            <span className="pp-chip-name">{activityNames[activity] || activity}</span>
                                            <span className="pp-chip-count">{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {(selectedActivityTypes.length > 0 || filterStartDate || filterEndDate) && (
                            <div className="pp-filter-controls">
                                <span className="pp-filter-label">Active</span>
                                <div className="pp-filters-list">
                                    {selectedActivityTypes.map(type => (
                                        <div key={type} className="pp-filter-chip">
                                            <span className="pp-chip-value">{activityNames[type] || type}</span>
                                            <button
                                                type="button"
                                                className="pp-chip-remove"
                                                onClick={() => handleActivityTypeClick(type)}
                                                aria-label={`Remove ${activityNames[type] || type} filter`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                    {filterStartDate && (
                                        <div className="pp-filter-chip">
                                            <span className="pp-chip-value">From {filterStartDate}</span>
                                            <button
                                                type="button"
                                                className="pp-chip-remove"
                                                onClick={() => setFilterStartDate('')}
                                                aria-label="Remove start date"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                    {filterEndDate && (
                                        <div className="pp-filter-chip">
                                            <span className="pp-chip-value">To {filterEndDate}</span>
                                            <button
                                                type="button"
                                                className="pp-chip-remove"
                                                onClick={() => setFilterEndDate('')}
                                                aria-label="Remove end date"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                    <button type="button" className="pp-clear-all" onClick={clearAllFilters}>
                                        Clear all
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="pp-date-filters">
                            <div className="pp-form-group">
                                <label htmlFor="profile-start-date">Start date</label>
                                <input
                                    id="profile-start-date"
                                    type="date"
                                    value={filterStartDate}
                                    onChange={e => setFilterStartDate(e.target.value)}
                                />
                            </div>
                            <div className="pp-form-group">
                                <label htmlFor="profile-end-date">End date</label>
                                <input
                                    id="profile-end-date"
                                    type="date"
                                    value={filterEndDate}
                                    onChange={e => setFilterEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        {activitiesLoading ? (
                            <div className="pp-empty">Loading activities...</div>
                        ) : filteredActivities.length === 0 ? (
                            <div className="pp-empty">No activities found</div>
                        ) : (
                            <div className="pp-activity-list">
                                {filteredActivities.map(a => (
                                    <button
                                        type="button"
                                        className="pp-activity-card"
                                        key={a.id}
                                        onClick={() => history.push(`/activity/${a.id}`)}
                                    >
                                        <span className="pp-activity-card-body">
                                            <span className="pp-activity-card-type">{a.activity_name || formatActivityName(a.activity)}</span>
                                            <span className="pp-activity-card-meta">
                                                <span>{new Date(a.date + 'T00:00').toLocaleDateString()}</span>
                                                {a.time != null && a.time > 0 && <span>{Math.round(a.time)} min</span>}
                                                {a.personal_effort != null && (
                                                    <span className="pp-effort-badge">Effort {a.personal_effort}/10</span>
                                                )}
                                            </span>
                                        </span>
                                        <span className="pp-activity-card-points">{a.points ?? 0} SP</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
